/**
 * React Query Hook: Search Metadata Items
 * 
 * Searches for metadata items by name (case-insensitive)
 * Returns top 20 matches ordered alphabetically
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { SEARCH_METADATA_ITEMS_QUERY } from '@/services/metadata_v1/queries';
import {
  SearchMetadataItem,
  SearchMetadataItemsResponse,
  SearchMetadataItemsVariables,
} from '@/services/metadata_v1/types';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

/**
 * Fetch search results from API
 */
async function fetchSearchResults(
  variables: SearchMetadataItemsVariables
): Promise<SearchMetadataItem[]> {
  const response = await graphqlRequest<SearchMetadataItemsResponse>(
    SEARCH_METADATA_ITEMS_QUERY,
    variables
  );
  return response.searchMetadataItems;
}

/**
 * Hook to search metadata items by name
 * 
 * Automatically uses organization ID from selected organization
 * 
 * @param searchString - The search query (minimum 2 characters recommended)
 * @param options - Query options
 * @returns React Query result with search results
 */
export function useSearchMetadataItems(
  searchString: string,
  options?: {
    enabled?: boolean;
  }
): UseQueryResult<SearchMetadataItem[], Error> {
  const { enabled = true } = options || {};
  
  // Subscribe to selected organization from auth store
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  
  // Extract IDs
  const organizationId = selectedOrg?.id;
  const retrievalId = selectedOrg?.latestMetadataRetrieval?.id;

  // Only search if query is at least 2 characters
  const shouldSearch = searchString.trim().length >= 2;

  return useQuery({
    queryKey: ['searchMetadataItems', organizationId, retrievalId, searchString],
    queryFn: () =>
      fetchSearchResults({
        organizationId: organizationId!,
        retrievalId: retrievalId!,
        searchString: searchString.trim(),
      }),
    enabled: enabled && !!organizationId && !!retrievalId && shouldSearch,
    staleTime: 30 * 1000, // 30 seconds - search results are relatively stable
    refetchOnWindowFocus: false,
  });
}